import { api } from '../api/client.ts';
import { platform } from '../platform.ts';
import { audioAnalysisPreferred } from './quality.ts';

/**
 * Audio levels measured by the proxy's ffmpeg, for devices that cannot listen
 * themselves.
 *
 * On a Google TV both ways of listening in the browser failed: routing the
 * video through WebAudio made the picture stutter every two seconds, and a
 * captureStream copy delivered nothing but silence. The proxy decodes the
 * sound anyway and reports its level ten times a second, stamped with
 * wall-clock time. A tile looks up the level at the PROGRAM-DATE-TIME it is
 * showing, so the cheer detection reacts to what is on screen -- not to what
 * the server heard ten seconds earlier.
 *
 * The desktop keeps listening locally: it works there and costs no requests.
 */
export const serverLevelsActive = platform !== 'desktop' && audioAnalysisPreferred();

const POLL_MS = 1000;
const KEEP_MS = 90_000;
/** A level older than this at the asked time counts as missing. */
const MAX_GAP_MS = 1000;

const series = new Map<string, Array<[number, number]>>();
const refs = new Map<string, number>();
let timer: number | null = null;
let polling = false;

/** Starts collecting for a session; balanced by release(). */
export function retain(sessionId: string): void {
  if (!serverLevelsActive) return;
  refs.set(sessionId, (refs.get(sessionId) ?? 0) + 1);
  timer ??= window.setInterval(() => void poll(), POLL_MS);
}

export function release(sessionId: string): void {
  const n = (refs.get(sessionId) ?? 0) - 1;
  if (n > 0) {
    refs.set(sessionId, n);
    return;
  }
  refs.delete(sessionId);
  series.delete(sessionId);
  if (refs.size === 0 && timer !== null) {
    clearInterval(timer);
    timer = null;
  }
}

/** dBFS at the given wall-clock time, or null when there is no fresh value. */
export function levelAt(sessionId: string, at: number): number | null {
  const points = series.get(sessionId);
  if (!points || points.length === 0) return null;
  // Newest first: the playhead is usually close to the end.
  for (let i = points.length - 1; i >= 0; i--) {
    const [t, db] = points[i]!;
    if (t <= at) return at - t <= MAX_GAP_MS ? db : null;
  }
  return null;
}

async function poll(): Promise<void> {
  if (polling || refs.size === 0) return;
  polling = true;
  try {
    // One request for all sessions; `since` is the oldest of their newest
    // points, and duplicates are skipped per session below.
    let since = Number.POSITIVE_INFINITY;
    for (const id of refs.keys()) since = Math.min(since, series.get(id)?.at(-1)?.[0] ?? 0);
    const result = await api.levels([...refs.keys()], Number.isFinite(since) ? since : 0);

    for (const [id, points] of Object.entries(result.levels)) {
      if (!refs.has(id)) continue;
      const list = series.get(id) ?? [];
      const last = list.at(-1)?.[0] ?? 0;
      for (const p of points) if (p[0] > last) list.push(p);
      const cutoff = result.now - KEEP_MS;
      let drop = 0;
      while (drop < list.length && list[drop]![0] < cutoff) drop++;
      if (drop > 0) list.splice(0, drop);
      series.set(id, list);
    }
  } catch {
    /* the next round tries again */
  } finally {
    polling = false;
  }
}
